import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Cpu, Network, Building2, HeartPulse, GraduationCap, Users, Landmark, ArrowRight, Plus, Equal, Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const PILLARS = [
  { icon: Network, analogue: "Care.com", role: "Marketplace", t5: "T5 Connect™",
    d: "A national two-sided marketplace matching families and organizations with vetted caregivers.",
    points: ["Caregiver profiles & matching", "Opportunity board (B2C/B2B/B2G)", "Availability & reviews"], link: "/marketplace" },
  { icon: Cpu, analogue: "WellSky", role: "B2B / B2G software, operations, analytics & AI", t5: "T5 Workforce™ + Intelligence™",
    d: "Operating software for agencies and public programs — scheduling, workforce analytics and AI decision support.",
    points: ["Workforce & scheduling", "Compliance across 50 states", "AI matching & BI dashboards"], link: "/businesses" },
  { icon: HeartPulse, analogue: "Honor", role: "Technology-powered care delivery", t5: "T5 Care™",
    d: "Coordinated, technology-enabled care delivery for families — intake, scheduling and communication in one place.",
    points: ["Digital intake", "Visit scheduling", "Family communication"], link: "/families" },
  { icon: GraduationCap, analogue: "T5 Academy", role: "Training & workforce development", t5: "T5 Academy™",
    d: "State-specific training, credentialing and continuing education that builds a job-ready care workforce.",
    points: ["Initial / annual / CE courses", "Credential tracking", "Bulk seat assignment"], link: "/training" },
];

const STAKEHOLDERS = [
  { icon: Users, t: "Families", d: "Find, book and manage trusted care.", link: "/families" },
  { icon: HeartPulse, t: "Caregivers", d: "Train, get matched and grow a career.", link: "/caregivers" },
  { icon: Building2, t: "Care companies", d: "Recruit, schedule, train and stay compliant.", link: "/businesses" },
  { icon: Landmark, t: "Public-sector programs", d: "Workforce, navigation and program analytics.", link: "/government" },
];

export default function Platform() {
  const navigate = useNavigate();
  return (
    <div>
      {/* HERO */}
      <section className="relative bg-blue-950 text-white overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
          <p className="text-xs uppercase tracking-[0.16em] text-teal-400 font-semibold mb-3 flex items-center gap-2"><Cpu className="w-4 h-4" /> The T5 Care Platform</p>
          <h1 className="font-display text-4xl lg:text-6xl font-bold max-w-4xl leading-[1.05]">The operating system for <span className="text-teal-400">care.</span></h1>
          <p className="mt-5 text-blue-100/85 max-w-2xl text-lg">T5 isn't a company that sends caregivers to homes. T5 is the operating system connecting families, caregivers, care companies and public-sector care programs — on one intelligent platform.</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button onClick={() => navigate("/request-demo")} className="h-12 px-7 bg-teal-500 hover:bg-teal-600 text-white">Request a Demo</Button>
            <Button onClick={() => navigate("/technology")} variant="outline" className="h-12 px-7 border-white/25 text-white bg-white/5 hover:bg-white/10">See the technology</Button>
          </div>
        </div>
      </section>

      {/* HUB & SPOKE DIAGRAM */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-14">
        <div className="relative w-full aspect-[600/380]" data-testid="platform-diagram">
          <svg viewBox="0 0 600 380" className="absolute inset-0 w-full h-full">
            {[[110, 80], [490, 80], [110, 300], [490, 300]].map(([x, y], i) => (
              <g key={i}>
                <line x1="300" y1="190" x2={x} y2={y} stroke="#0d9488" strokeWidth="2" strokeOpacity="0.35" strokeDasharray="4 4" />
                <motion.circle r="5" fill="#2dd4bf"
                  initial={{ cx: 300, cy: 190 }}
                  animate={{ cx: [300, x, 300], cy: [190, y, 190] }}
                  transition={{ duration: 3, repeat: Infinity, delay: i * 0.5, ease: "easeInOut" }} />
              </g>
            ))}
          </svg>
          {/* center hub */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 2.4, repeat: Infinity }}
              className="grid place-items-center w-28 h-28 rounded-full bg-blue-950 text-white text-center shadow-xl ring-8 ring-teal-500/10">
              <div><Cpu className="w-6 h-6 text-teal-400 mx-auto mb-1" /><p className="font-display font-bold text-xs leading-tight">T5 Care<br />Platform</p></div>
            </motion.div>
          </div>
          {/* nodes */}
          {STAKEHOLDERS.map((s, i) => {
            const pos = [{ left: "18.3%", top: "21%" }, { left: "81.7%", top: "21%" }, { left: "18.3%", top: "79%" }, { left: "81.7%", top: "79%" }][i];
            return (
              <div key={s.t} data-testid={`diagram-node-${i}`} className="absolute -translate-x-1/2 -translate-y-1/2 z-10" style={pos}>
                <div className="bg-white rounded-xl border border-blue-900/5 shadow-md px-3 py-2 flex items-center gap-2 w-40">
                  <span className="grid place-items-center w-8 h-8 rounded-lg bg-teal-50 text-teal-700 shrink-0"><s.icon className="w-4 h-4" /></span>
                  <span className="text-xs font-semibold text-blue-950 leading-tight">{s.t}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* THE EQUATION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.18em] text-teal-600 font-semibold mb-2">Four categories. One platform.</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-blue-950">The best of every care category, unified.</h2>
          <p className="mt-3 text-slate-500 max-w-2xl mx-auto">Each pillar of the care industry has its own leader. T5 brings them together into a single operating system.</p>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="platform-pillars">
          {PILLARS.map((p, i) => (
            <motion.div key={p.t5} data-testid={`pillar-${i}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.06 }}
              className="relative bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm flex flex-col">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-50 text-teal-700 mb-4"><p.icon className="w-6 h-6" /></span>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Like {p.analogue}</p>
              <p className="text-xs text-slate-500 mb-2">{p.role}</p>
              <h3 className="font-display text-lg font-bold text-blue-950">{p.t5}</h3>
              <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{p.d}</p>
              <ul className="mt-3 space-y-1.5 flex-1">{p.points.map((pt) => <li key={pt} className="flex items-center gap-2 text-sm text-slate-600"><Check className="w-4 h-4 text-teal-500 shrink-0" /> {pt}</li>)}</ul>
              <Link to={p.link} className="mt-4 inline-flex items-center gap-1.5 text-blue-900 font-semibold text-sm hover:gap-2.5 transition-all">Explore <ArrowRight className="w-4 h-4" /></Link>
              {i < PILLARS.length - 1 && <span className="hidden xl:grid place-items-center absolute -right-3.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-blue-950 text-teal-400 z-10"><Plus className="w-4 h-4" /></span>}
            </motion.div>
          ))}
        </div>
        <div className="mt-8 flex items-center justify-center gap-3">
          <span className="grid place-items-center w-9 h-9 rounded-full bg-blue-950 text-teal-400"><Equal className="w-5 h-5" /></span>
          <p className="font-display text-xl font-bold text-blue-950">The T5 Care Platform</p>
        </div>
      </section>

      {/* CONNECTS EVERYONE */}
      <section className="bg-slate-50 border-y border-blue-900/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-blue-950">One system connecting <span className="text-teal-600">everyone in care.</span></h2>
            <p className="mt-3 text-slate-500 max-w-2xl mx-auto">Families, caregivers, care companies and government share the same data, workflows and standards — so care is coordinated end to end.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="platform-stakeholders">
            {STAKEHOLDERS.map((s, i) => (
              <motion.div key={s.t} data-testid={`stakeholder-${i}`} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm text-center">
                <span className="grid place-items-center w-14 h-14 rounded-2xl bg-blue-950 text-teal-400 mx-auto mb-4"><s.icon className="w-7 h-7" /></span>
                <h3 className="font-display font-bold text-blue-950">{s.t}</h3>
                <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{s.d}</p>
                <Link to={s.link} className="mt-3 inline-flex items-center gap-1.5 text-teal-700 font-semibold text-sm hover:gap-2.5 transition-all">Enter <ArrowRight className="w-4 h-4" /></Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-3xl bg-blue-950 text-white p-10 lg:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-72 h-72 bg-teal-500/20 rounded-full blur-3xl" />
          <Sparkles className="w-8 h-8 text-teal-400 mx-auto mb-4 relative" />
          <h2 className="font-display text-3xl lg:text-4xl font-bold relative">Run your entire care operation on T5.</h2>
          <p className="mt-3 text-blue-100/80 max-w-2xl mx-auto relative">From the first care request to a fully trained, compliant, scheduled workforce — one platform, nationwide.</p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center relative">
            <Button onClick={() => navigate("/request-demo")} className="h-12 px-7 bg-teal-500 hover:bg-teal-600 text-white">Request a Demo</Button>
            <Button onClick={() => navigate("/compliance")} variant="outline" className="h-12 px-7 border-white/25 text-white bg-white/5 hover:bg-white/10">Explore Compliance</Button>
          </div>
          <div className="mt-5 relative"><Link to="/investors" className="text-sm text-teal-300 font-medium inline-flex items-center gap-1 hover:gap-1.5 transition-all">For partners &amp; investors <ArrowRight className="w-4 h-4" /></Link></div>
        </div>
      </section>
    </div>
  );
}
